/**
 * Admin Google Analytics Section - Website Traffic, Page Views, User Behavior, Conversions
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { 
  LineChart, Users, Eye, MousePointer, Clock, Globe,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Monitor, Smartphone, Tablet, RefreshCw, Loader2, Calendar,
  MapPin, ExternalLink, Target
} from 'lucide-react';

const AdminGoogleAnalyticsSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7days');
  
  // Mock Google Analytics Data
  const [stats, setStats] = useState({
    pageViews: 12847,
    uniqueVisitors: 3256,
    avgSessionDuration: '2m 34s',
    bounceRate: 42.3,
    newUsers: 1892,
    returningUsers: 1364
  });

  const [topPages, setTopPages] = useState([
    { page: '/', title: 'Home', views: 4521, uniqueViews: 2103, avgTime: '1m 45s', bounceRate: 38 },
    { page: '/build', title: 'Build - Signature Program', views: 2847, uniqueViews: 1456, avgTime: '3m 12s', bounceRate: 28 },
    { page: '/protect', title: 'Protect - Dispatch', views: 1923, uniqueViews: 987, avgTime: '2m 58s', bounceRate: 35 },
    { page: '/grow', title: 'Grow - Scaling', views: 1456, uniqueViews: 823, avgTime: '2m 21s', bounceRate: 41 },
    { page: '/academy', title: 'Queen B Academy', views: 1289, uniqueViews: 756, avgTime: '4m 05s', bounceRate: 22 },
    { page: '/toolkit', title: 'Queen B Resources', views: 892, uniqueViews: 534, avgTime: '3m 45s', bounceRate: 31 },
    { page: '/hive/dashboard', title: 'Hive Dashboard', views: 756, uniqueViews: 312, avgTime: '8m 23s', bounceRate: 12 },
    { page: '/contact', title: 'Contact Us', views: 543, uniqueViews: 421, avgTime: '1m 12s', bounceRate: 56 }
  ]);

  const [trafficSources, setTrafficSources] = useState([
    { source: 'Google Search', sessions: 1456, percentage: 44.7, change: 12.3 },
    { source: 'Direct', sessions: 892, percentage: 27.4, change: 5.2 },
    { source: 'Social Media', sessions: 534, percentage: 16.4, change: -3.1 },
    { source: 'Referral', sessions: 267, percentage: 8.2, change: 8.7 },
    { source: 'Email', sessions: 107, percentage: 3.3, change: 15.4 }
  ]);

  const [deviceBreakdown, setDeviceBreakdown] = useState([
    { device: 'Desktop', icon: Monitor, sessions: 1823, percentage: 56, color: '#3B82F6' },
    { device: 'Mobile', icon: Smartphone, sessions: 1156, percentage: 35.5, color: '#10B981' },
    { device: 'Tablet', icon: Tablet, sessions: 277, percentage: 8.5, color: '#F59E0B' }
  ]);

  const [topLocations, setTopLocations] = useState([
    { country: 'United States', sessions: 2134, percentage: 65.5 },
    { country: 'Canada', sessions: 456, percentage: 14.0 },
    { country: 'Mexico', sessions: 234, percentage: 7.2 },
    { country: 'United Kingdom', sessions: 198, percentage: 6.1 },
    { country: 'Australia', sessions: 134, percentage: 4.1 },
    { country: 'Other', sessions: 100, percentage: 3.1 }
  ]);

  const [conversions, setConversions] = useState([
    { goal: 'Carrier Application Started', completions: 156, rate: 4.8, value: '$0' },
    { goal: 'Academy Course Purchase', completions: 23, rate: 0.7, value: '$6,877' },
    { goal: 'Resource Download', completions: 47, rate: 1.4, value: '$2,350' },
    { goal: 'Contact Form Submitted', completions: 89, rate: 2.7, value: '$0' },
    { goal: 'Hive Account Created', completions: 34, rate: 1.0, value: '$0' }
  ]);

  const [realtimeUsers, setRealtimeUsers] = useState(23);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
    
    // Simulate realtime updates
    const interval = setInterval(() => {
      setRealtimeUsers(prev => Math.max(5, prev + Math.floor(Math.random() * 7) - 3));
    }, 5000);
    
    return () => clearInterval(interval);
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Google Analytics" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#EA4335] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Google Analytics" 
      subtitle="Website traffic, page views, user behavior, and conversion tracking"
    >
      {/* Date Range Selector & Realtime */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-white/50" />
          <select 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-[#121212] border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
        </div>
        
        {/* Realtime Users */}
        <div className="flex items-center gap-3 px-4 py-2 bg-[#EA4335]/10 border border-[#EA4335]/30">
          <div className="w-2 h-2 bg-[#EA4335] rounded-full animate-pulse" />
          <span className="text-white/70 text-sm">Realtime:</span>
          <span className="text-[#EA4335] font-bold text-lg">{realtimeUsers}</span>
          <span className="text-white/50 text-sm">users online</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Eye} label="Page Views" value={stats.pageViews.toLocaleString()} change="+8.3%" up={true} color="#EA4335" onClick={() => setActiveTab('pages')} />
        <StatCard icon={Users} label="Visitors" value={stats.uniqueVisitors.toLocaleString()} change="+12.1%" up={true} color="#4285F4" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Clock} label="Avg Session" value={stats.avgSessionDuration} change="+5.2%" up={true} color="#FBBC04" onClick={() => setActiveTab('overview')} />
        <StatCard icon={MousePointer} label="Bounce Rate" value={`${stats.bounceRate}%`} change="-2.4%" up={true} color="#34A853" onClick={() => setActiveTab('overview')} />
        <StatCard icon={TrendingUp} label="New Users" value={stats.newUsers.toLocaleString()} change="+15.7%" up={true} color="#EA4335" onClick={() => setActiveTab('traffic')} />
        <StatCard icon={Users} label="Returning" value={stats.returningUsers.toLocaleString()} change="+3.2%" up={true} color="#4285F4" onClick={() => setActiveTab('traffic')} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: LineChart },
          { id: 'pages', label: 'Top Pages', icon: Eye },
          { id: 'traffic', label: 'Traffic Sources', icon: Globe },
          { id: 'conversions', label: 'Conversions', icon: Target },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'text-[#EA4335] bg-[#EA4335]/10 border border-[#EA4335]/30'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Traffic Chart Placeholder */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4">Traffic Overview (Last 7 Days)</h3>
            <div className="h-64 bg-[#0A0A0A] border border-white/5 flex items-center justify-center relative">
              {/* Simulated chart bars */}
              <div className="flex items-end gap-4 h-48">
                {[65, 78, 45, 89, 72, 95, 82].map((height, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-2">
                    <div 
                      className="w-12 bg-gradient-to-t from-[#EA4335] to-[#EA4335]/50 transition-all hover:from-[#EA4335] hover:to-[#EA4335]/70"
                      style={{ height: `${height * 1.8}px` }}
                    />
                    <span className="text-white/30 text-xs">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device Breakdown */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-4">Device Breakdown</h3>
              <div className="space-y-4">
                {deviceBreakdown.map((device) => {
                  const Icon = device.icon;
                  return (
                    <div key={device.device} className="flex items-center gap-4">
                      <div className="p-2 rounded" style={{ backgroundColor: `${device.color}20` }}>
                        <Icon className="w-5 h-5" style={{ color: device.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white">{device.device}</span>
                          <span className="text-white/70">{device.percentage}%</span>
                        </div>
                        <div className="h-2 bg-white/10 overflow-hidden">
                          <div 
                            className="h-full transition-all"
                            style={{ width: `${device.percentage}%`, backgroundColor: device.color }}
                          />
                        </div>
                      </div>
                      <span className="text-white/50 text-sm">{device.sessions.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Locations */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-4">Top Locations</h3>
              <div className="space-y-3">
                {topLocations.map((location, idx) => (
                  <div key={location.country} className="flex items-center justify-between p-2 bg-[#0A0A0A] border border-white/5">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#EA4335]/20 text-[#EA4335] text-xs flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-white">{location.country}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-medium">{location.sessions.toLocaleString()}</span>
                      <span className="text-white/40 text-sm ml-2">({location.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Pages Tab */}
      {activeTab === 'pages' && (
        <div className="bg-[#121212] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-white font-bold">Top Pages by Views</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase p-3">Page</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Views</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Unique</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Avg. Time</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Bounce</th>
                </tr>
              </thead>
              <tbody>
                {topPages.map((page) => (
                  <tr key={page.page} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div>
                        <p className="text-white font-medium">{page.title}</p>
                        <p className="text-white/40 text-xs">{page.page}</p>
                      </div>
                    </td>
                    <td className="p-3 text-right text-[#EA4335] font-medium">{page.views.toLocaleString()}</td>
                    <td className="p-3 text-right text-white/70">{page.uniqueViews.toLocaleString()}</td>
                    <td className="p-3 text-right text-white/70">{page.avgTime}</td>
                    <td className="p-3 text-right">
                      <span className={page.bounceRate < 35 ? 'text-green-400' : page.bounceRate < 50 ? 'text-yellow-400' : 'text-red-400'}>
                        {page.bounceRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Traffic Sources Tab */}
      {activeTab === 'traffic' && (
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">Traffic Sources</h3>
          <div className="space-y-4">
            {trafficSources.map((source) => (
              <div key={source.source} className="bg-[#0A0A0A] border border-white/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#EA4335]" />
                    <span className="text-white font-medium">{source.source}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white font-bold">{source.sessions.toLocaleString()}</span>
                    <span className={`flex items-center gap-1 text-sm ${source.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {source.change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {Math.abs(source.change)}%
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-white/10 overflow-hidden">
                  <div 
                    className="h-full bg-[#EA4335]"
                    style={{ width: `${source.percentage}%` }}
                  />
                </div>
                <p className="text-white/40 text-xs mt-1">{source.percentage}% of total traffic</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversions Tab */}
      {activeTab === 'conversions' && (
        <div className="bg-[#121212] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-white font-bold">Goal Conversions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase p-3">Goal</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Completions</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Conv. Rate</th>
                  <th className="text-right text-white/50 text-xs uppercase p-3">Value</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map((goal, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <Target className="w-5 h-5 text-[#EA4335]" />
                        <span className="text-white font-medium">{goal.goal}</span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-white font-bold">{goal.completions}</td>
                    <td className="p-3 text-right text-[#EA4335]">{goal.rate}%</td>
                    <td className="p-3 text-right text-green-400 font-medium">{goal.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Total Conversions */}
          <div className="p-4 bg-[#0A0A0A] border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-white font-bold">Total Conversions</span>
              <div className="flex items-center gap-6">
                <span className="text-white font-bold">{conversions.reduce((acc, c) => acc + c.completions, 0)}</span>
                <span className="text-green-400 font-bold">$9,227</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Stat Card Component - Clickable
const StatCard = ({ icon: Icon, label, value, change, up, color, onClick }) => (
  <div 
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    className={`bg-[#121212] border border-white/10 p-4 select-none ${onClick ? 'cursor-pointer hover:bg-[#1a1a1a] hover:border-white/20 active:bg-[#222] transition-all' : ''}`}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick && onClick()}
  >
    <div className="flex items-center gap-2 mb-2 pointer-events-none">
      <div className="p-1.5 rounded flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span className="text-white/50 text-xs">{label}</span>
    </div>
    <p className="text-xl font-bold text-white mb-1 pointer-events-none">{value}</p>
    <span className={`flex items-center gap-1 text-xs pointer-events-none ${up ? 'text-green-400' : 'text-red-400'}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {change}
    </span>
  </div>
);

export default AdminGoogleAnalyticsSection;
